/**
 * Honest Buy / Rent button states — pure, unit-tested.
 *
 * The backend's `in_stock_now` / `available_units` (ONE rule: the same
 * check the add-to-cart refusal runs) decides whether a button may be
 * enabled. Nothing here short-circuits to "in stock" on
 * manage_inventory=false or allow_backorder when the backend already
 * answered: a clickable Buy that will fail is never rendered.
 */

import type { RentalInfo } from "@/types/rental"

export type StockFields = {
  in_stock_now?: boolean
  available_units?: number
  /**
   * How many the shop can ship as ONE line. Medusa reserves a line from a single
   * warehouse, so five copies split 3 + 2 between Praha and Brno can be ordered
   * three at a time, not four — the backend sends both numbers and the add-to-cart
   * refusal uses this one.
   */
  fulfillable_units?: number
}

/** True only when the backend answered with the boolean. */
export function backendInStock(p: StockFields | null | undefined): boolean | undefined {
  return typeof p?.in_stock_now === "boolean" ? p.in_stock_now : undefined
}

/** available_units when the backend sent a number; undefined otherwise. */
export function availableUnits(p: StockFields | null | undefined): number | undefined {
  const n = p?.available_units
  return typeof n === "number" && Number.isFinite(n) ? n : undefined
}

/**
 * The number a customer can actually put in the cart at once: the smaller of the
 * total and what one warehouse holds. Falls back to the total when the backend
 * sent no split (older responses), so nothing becomes stricter by accident.
 */
export function orderableUnits(p: StockFields | null | undefined): number | undefined {
  const total = availableUnits(p)
  const perWarehouse = p?.fulfillable_units
  if (typeof perWarehouse !== "number" || !Number.isFinite(perWarehouse)) return total
  return typeof total === "number" ? Math.min(total, perWarehouse) : perWarehouse
}

// ── Product page ─────────────────────────────────────────────────────────────

export type PageVariantLike = {
  manage_inventory?: boolean | null
  allow_backorder?: boolean | null
  inventory_quantity?: number | null
} | null | undefined

/**
 * Product page: stock for the selected variant.
 *  - no variant selected → false (nothing to add)
 *  - backend answered   → that answer, full stop
 *  - no answer (endpoint without enrichment) → legacy Medusa heuristic
 */
export function resolvePageInStock(product: StockFields | null | undefined, variant: PageVariantLike): boolean {
  if (!variant) return false
  const answered = backendInStock(product)
  if (answered !== undefined) return answered
  if (!variant.manage_inventory) return true
  if (variant.allow_backorder) return true
  return (variant.inventory_quantity || 0) > 0
}

// ── Catalog card ─────────────────────────────────────────────────────────────

export type BuyAction =
  | "add" // add to cart right on the card
  | "choose" // several variants → the only case that leaves the card
  | "outOfStock" // disabled "Нет в наличии"
  | "hidden"

export type RentAction =
  | "dialog" // quick-rent dialog right on the card
  | "outOfStock"
  | "hidden"

export type CardActions = { buy: BuyAction; rent: RentAction }

export type CardActionsInput = {
  inStock: boolean
  /** default variant id (variants[0]) when the product has variants */
  variantId?: string | null
  /** how many variants the product has (>1 = customer must choose) */
  variantCount: number
  rentalInfo?: RentalInfo | null
}

export function showBuyButton(rentalInfo?: RentalInfo | null): boolean {
  return (
    !rentalInfo ||
    !rentalInfo.isRentalAvailable ||
    rentalInfo.availabilityMode === "sale_only" ||
    rentalInfo.availabilityMode === "both"
  )
}

export function showRentButton(rentalInfo?: RentalInfo | null): boolean {
  return (
    !!rentalInfo?.isRentalAvailable &&
    (rentalInfo.availabilityMode === "rental_only" || rentalInfo.availabilityMode === "both")
  )
}

/**
 * Card buttons never navigate on Buy/Rent except the one honest case:
 * the product has several variants and the card can't pick for the
 * customer → "Выбрать" links to the page.
 */
export function resolveCardActions(input: CardActionsInput): CardActions {
  const wantsBuy = showBuyButton(input.rentalInfo)
  const wantsRent = showRentButton(input.rentalInfo)
  const hasVariant = !!input.variantId
  const needsChoice = input.variantCount > 1

  let buy: BuyAction = "hidden"
  if (wantsBuy) {
    if (!input.inStock || !hasVariant) buy = "outOfStock"
    else if (needsChoice) buy = "choose"
    else buy = "add"
  }

  let rent: RentAction = "hidden"
  if (wantsRent) {
    // rentalInfo is non-null whenever wantsRent; the dialog picks the
    // variant itself (variantIdHint is optional) — no page hop needed.
    rent = !input.inStock || !hasVariant ? "outOfStock" : "dialog"
  }

  return { buy, rent }
}
